/**
 * Referral link helpers for the "Help a friend" / "Share Louise" flow.
 *
 * Every logged-in user gets a stable, per-user referral code so we can track
 * which user sent a given invite (and, right now, who entered the "win a free
 * cycle" sweepstakes by sharing). The code is embedded in the shared URL, read
 * back on the landing route (`invite/:code?`), and joined in PostHog to the
 * inviter via the `inviter_patient_id` we attach to the share event.
 *
 * INTERIM: the code is derived deterministically from the user's Dashboard API
 * patient id on the client, so referral tracking works today with zero backend.
 * The durable design is a server-minted opaque code via `POST /invites` (see
 * docs/features/PEER_INVITE_BACKEND.md + useCreateInviteCode.ts). When that
 * endpoint ships, swap `referralCodeForPatient` for the minted code; the rest
 * of the flow (URL shape, landing route, analytics) stays the same.
 */

/** Web origin that hosts the invite landing route (also the deployed web app). */
export const REFERRAL_ORIGIN = "https://my.louise.life";

/**
 * Current incentive attached to sharing. Kept as a stable id so the funnel
 * survives incentive changes: if the "free cycle" sweepstakes is replaced,
 * bump this and past entries stay segmentable by their original campaign.
 */
export const REFERRAL_CAMPAIGN_ID = "free_cycle";

/**
 * Deterministic, dependency-free hash of the patient id into a short URL-safe
 * code. Two 32-bit FNV-1a passes with different offsets give ~64 bits, so
 * collisions are negligible at our scale, and the same user always produces the
 * same code (links stay stable and dedupable). It is opaque, not reversible:
 * the code -> inviter mapping comes from the `inviter_patient_id` we send on the
 * share event, never from decoding the code. We intentionally do not put the
 * raw patient id in a shareable URL.
 */
export function referralCodeForPatient(patientId: string): string {
  const fnv1a = (input: string, offset: number): number => {
    let hash = offset;
    for (let i = 0; i < input.length; i += 1) {
      hash ^= input.charCodeAt(i);
      // 32-bit FNV prime multiply via shifts to stay in integer range.
      hash = Math.imul(hash, 0x01000193);
    }
    return hash >>> 0;
  };

  const high = fnv1a(patientId, 0x811c9dc5).toString(36);
  const low = fnv1a(patientId, 0x1000193).toString(36);
  return `${high}${low}`;
}

/**
 * Build the full shareable URL for a code. The referral is also tagged as an
 * acquisition source (`utm_source=referral`) so it shows up in the same
 * "who came from where" reporting as social, search, etc., while the code adds
 * per-inviter granularity on top.
 */
export function buildReferralUrl(code: string): string {
  const params = new URLSearchParams({
    utm_source: "referral",
    utm_medium: "invite",
    utm_campaign: REFERRAL_CAMPAIGN_ID,
  });
  return `${REFERRAL_ORIGIN}/invite/${code}?${params.toString()}`;
}
